import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useUser, useUserUpadateUser } from "@/features/auth/repositories/auth-repository";
import { IdentificationSchema, TIdentificationSchema } from "@/features/settings/forms/schema";
import { buildAccountFormData } from "@/features/settings/account-payload";

import Button from "@/components/buttons/Button";
import SelectInput from "@/components/form-controls/SelectInput";
import TextInput from "@/components/form-controls/TextInput";


const nationalities = [
    { label: "Tanzanian", value: "Tanzanian" },
    { label: "Non Tanzanian", value: "Non Tanzanian" }
]

const identificationTypes = [
    { label: "NIDA", value: "nida" },
    { label: "Passport", value: "passport" },
    { label: "Driving Licence", value: "driving-licence" }
]


export default function Identification() {
    const { user, isLoading } = useUser();
    const account = user?.results;

    const { mutate: updateUser, isPending } = useUserUpadateUser();

    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(IdentificationSchema),
        values: {
            nationality: account?.nationality ?? "",
            dateOfBirth: account?.dateOfBirth ?? "",
            identificationType: account?.identificationType ?? "",
            identificationNumber: account?.identificationNumber ?? ""
        },
        resetOptions: { keepDirtyValues: true }
    });

    const submit = (data: TIdentificationSchema) => {
        if (!account) return;

        updateUser({
            id: account.id,
            input: buildAccountFormData(account, { account: data })
        });
    }

    const documents = account?.identificationImages ?? [];

    return (
        <div className="w-full py-3 pr-3 space-y-6">
            <div>
                <h1 className="text-lg font-semibold">Identification Details</h1>
                <p className="text-xs text-secondary-300">
                    The identity ZCT checks your licence and investment applications against.
                </p>
            </div>

            <form onSubmit={handleSubmit(submit)} className="grid grid-cols-2 gap-4">
                <SelectInput
                    label="Nationality"
                    displayName="label"
                    valueName="value"
                    options={nationalities}
                    error={errors.nationality?.message?.toString()}
                    register={register("nationality")}
                    required
                />

                <TextInput
                    label="Date of birth"
                    type="date"
                    error={errors.dateOfBirth?.message?.toString()}
                    register={register("dateOfBirth")}
                />

                <SelectInput
                    label="Identification Type"
                    displayName="label"
                    valueName="value"
                    options={identificationTypes}
                    error={errors.identificationType?.message?.toString()}
                    register={register("identificationType")}
                    required
                />

                <TextInput
                    label="Identification Number"
                    placeholder="enter identification number"
                    error={errors.identificationNumber?.message?.toString()}
                    register={register("identificationNumber")}
                    required
                />

                <div className="col-span-2">
                    <p className="text-sm font-medium text-secondary mb-1">
                        Identification document
                    </p>

                    {
                        documents.length > 0 ? (
                            <ul className="text-sm space-y-1">
                                {
                                    documents.map((document, index) => (
                                        <li key={index}>
                                            <a
                                                href={document}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-primary hover:underline"
                                            >
                                                Document {index + 1}
                                            </a>
                                        </li>
                                    ))
                                }
                            </ul>
                        ) : (
                            <p className="text-xs text-secondary-300">
                                No document is on file for this account.
                            </p>
                        )
                    }

                    <p className="text-xs text-secondary-300 mt-1">
                        Documents are captured at registration. Replacing one is not available
                        here yet - contact ZCT to have it updated.
                    </p>
                </div>

                <div className="col-span-2 flex justify-end">
                    <Button
                        type="submit"
                        className="min-w-32"
                        intent="primary"
                        loading={isPending}
                        disabled={isLoading || !account}
                    >
                        Save Changes
                    </Button>
                </div>
            </form>
        </div>
    )
}
